// The headless Studio: what is on the shelf, what the user is doing, and the
// intents that change either. It knows nothing about rooms, cameras or the
// DOM; presenters subscribe and draw.

import { DEVICE_IDS, type AppInfo, type DeviceId, type DeviceInfo, type NativeTarget, type StudioSnapshot } from "./types.ts";

export type Focus =
  | { kind: "room" }
  | { kind: "device"; device: DeviceId }
  | { kind: "app"; app: string }
  | { kind: "laptop" }
  /** The closet: the devices on the shelf, with their colours to choose from. */
  | { kind: "closet" }
  /** The television: the feed of games the community has published. */
  | { kind: "tv" }
  | { kind: "play"; device: DeviceId; app: string }
  /** Making an app for the Tab5 (docs/make.md): one of the user's Tab5 apps, or a new one not made yet (`app` null). */
  | { kind: "make"; app: string | null };

export interface StudioState {
  snapshot: StudioSnapshot;
  focus: Focus;
}

/** A room the account does not own, or something only members have. */
export class PaymentRequired extends Error {
  constructor(readonly kind: "room" | "membership", readonly id: string, readonly price: number) {
    super(`${kind} ${id} is not owned`);
  }
}

/** An account is needed for what was asked: a game's package. A guest's room is asked to sign in. */
export class SignInRequired extends Error {
  constructor() {
    super("an account is needed");
  }
}

/**
 * The backend said no and why: its author has not allowed downloads, the
 * room has not shipped. `message` is a sentence fit to show the visitor.
 */
export class Refused extends Error {}

/** A package the backend agreed to give: where the browser fetches it, and what the file is. */
export interface PackageDownload {
  url: string;
  filename: string;
  size: number;
}

/** Where snapshots come from and where changes go: the HTTP API, or a local stand-in. */
export interface StudioBackend {
  snapshot(): Promise<StudioSnapshot>;
  /** Wear a catalog skin, or the account's own `color` under CUSTOM_SKIN. Rejects with PaymentRequired for a member colour and no membership. */
  setSkin(device: DeviceId, skin: string, color?: string): Promise<StudioSnapshot>;
  setRoom(room: string): Promise<StudioSnapshot>;
  /** Give one of the user's own games another title. Rejects with Refused when it is not 1 to 60 characters. */
  setTitle(app: string, title: string): Promise<StudioSnapshot>;
  setPublished(app: string, on: boolean): Promise<StudioSnapshot>;
  /** Let members download the installation packages of one of the user's own games, or stop it. */
  setNativeAllowed(app: string, on: boolean): Promise<StudioSnapshot>;
  /** Move one of the user's own published games to another address label. Rejects with Refused when the label is taken or not allowed. */
  setSlug(app: string, slug: string): Promise<StudioSnapshot>;
  /**
   * Ask for a game's installation package for a target. Resolves with the
   * address the browser downloads it from: a package can be hundreds of
   * megabytes, so the page never holds its bytes. Rejects with
   * PaymentRequired for a non-member, and with Refused when the author has
   * not allowed downloads or has uploaded no package for that target.
   */
  nativeDownload(app: string, device: NativeTarget): Promise<PackageDownload>;
  /**
   * Give one of the user's own games the address `slug`, the label of its
   * game host. Rejects with Refused when the label is not valid, is taken, or
   * the user is a guest.
   */
  setSlug(app: string, slug: string): Promise<StudioSnapshot>;
  /**
   * Delete one of the user's own games for good: its builds, packages, web
   * build and sessions, and its place on the shelf. Its address stays held
   * for the account for 30 days.
   */
  deleteApp(app: string): Promise<StudioSnapshot>;
  /**
   * Give the account the handle its games are signed with. Rejects with
   * Refused when the name is not a handle, is reserved, is another account's,
   * or the user is a guest.
   */
  setHandle(handle: string): Promise<StudioSnapshot>;
  /**
   * Put another author's published game on the shelf (`on`), from its link
   * or from Pocket TV (`via`), or take a game the user kept off again.
   * Rejects with Refused at the shelf's limit.
   */
  setKept(app: string, on: boolean, via: "link" | "tv"): Promise<StudioSnapshot>;
  signOut(): Promise<void>;
}

export type Listener = (state: StudioState, previous: StudioState) => void;

export class StudioCore {
  private listeners = new Set<Listener>();
  state: StudioState;

  constructor(private backend: StudioBackend, snapshot: StudioSnapshot) {
    this.state = { snapshot, focus: { kind: "room" } };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private set(next: Partial<StudioState>): void {
    const previous = this.state;
    this.state = { ...previous, ...next };
    for (const listener of this.listeners) listener(this.state, previous);
  }

  // ------------------------------------------------------------ queries

  device(id: DeviceId): DeviceInfo {
    const device = this.state.snapshot.devices.find((entry) => entry.id === id);
    if (!device) throw new Error(`unknown device ${id}`);
    return device;
  }

  /** A game the room can open: one on the shelf, or one Pocket TV or a link shows that is not on it. */
  app(id: string): AppInfo | undefined {
    return this.state.snapshot.apps.find((entry) => entry.id === id) ?? this.state.snapshot.aired?.find((entry) => entry.id === id);
  }

  /** Whether a game is on the shelf: its case on the rack, its name on its devices' cards. */
  onShelf(id: string): boolean {
    return this.state.snapshot.apps.some((entry) => entry.id === id);
  }

  /** Apps with a build for the device, the user's own first. */
  appsFor(device: DeviceId): AppInfo[] {
    const rank = { mine: 0, starter: 1, shared: 2 } as const;
    return this.state.snapshot.apps
      .filter((app) => app.devices.includes(device))
      .sort((a, b) => rank[a.origin] - rank[b.origin]);
  }

  devicesFor(app: string): DeviceInfo[] {
    const entry = this.app(app);
    if (!entry) return [];
    return DEVICE_IDS.filter((id) => entry.devices.includes(id)).map((id) => this.device(id));
  }

  // ------------------------------------------------------------ intents

  selectDevice(device: DeviceId): void {
    this.set({ focus: { kind: "device", device } });
  }

  selectApp(app: string): void {
    if (this.app(app)) this.set({ focus: { kind: "app", app } });
  }

  openLaptop(): void {
    this.set({ focus: { kind: "laptop" } });
  }

  openCloset(): void {
    this.set({ focus: { kind: "closet" } });
  }

  openTv(): void {
    this.set({ focus: { kind: "tv" } });
  }

  /** Opens the workspace on one of the user's Tab5 apps, or on a new one (null). */
  openMake(app: string | null): void {
    this.set({ focus: { kind: "make", app } });
  }

  /** Step out of whatever is focused, back to the room. */
  back(): void {
    if (this.state.focus.kind !== "room") this.set({ focus: { kind: "room" } });
  }

  /** Play an app. Without a device, the focused device is used if it can run the app, else the first that can. */
  play(app: string, device?: DeviceId): boolean {
    const entry = this.app(app);
    if (!entry) return false;
    const focus = this.state.focus;
    const preferred = device ?? (focus.kind === "device" ? focus.device : undefined);
    const chosen = preferred && entry.devices.includes(preferred)
      ? preferred
      : DEVICE_IDS.find((id) => entry.devices.includes(id));
    if (!chosen) return false;
    this.set({ focus: { kind: "play", device: chosen, app } });
    return true;
  }

  async setKept(app: string, on: boolean, via: "link" | "tv"): Promise<void> {
    const before = this.app(app);
    const snapshot = await this.backend.setKept(app, on, via);
    // A game taken off the shelf stays open on its card, where it can be put back.
    const openable = snapshot.apps.some((entry) => entry.id === app) || snapshot.aired?.some((entry) => entry.id === app);
    if (!on && before && !openable) snapshot.aired = [{ ...before, kept: false }, ...(snapshot.aired ?? [])];
    this.set({ snapshot });
  }

  async setTitle(app: string, title: string): Promise<void> {
    this.set({ snapshot: await this.backend.setTitle(app, title) });
  }

  async setSkin(device: DeviceId, skin: string, color?: string): Promise<void> {
    this.set({ snapshot: await this.backend.setSkin(device, skin, color) });
  }

  async setRoom(room: string): Promise<void> {
    this.set({ snapshot: await this.backend.setRoom(room) });
  }

  async setPublished(app: string, on: boolean): Promise<void> {
    this.set({ snapshot: await this.backend.setPublished(app, on) });
  }

  async setNativeAllowed(app: string, on: boolean): Promise<void> {
    this.set({ snapshot: await this.backend.setNativeAllowed(app, on) });
  }

  nativeDownload(app: string, device: NativeTarget): Promise<PackageDownload> {
    return this.backend.nativeDownload(app, device);
  }

  async setSlug(app: string, slug: string): Promise<void> {
    this.set({ snapshot: await this.backend.setSlug(app, slug) });
  }

  /** Delete one of the user's own games. Whatever was showing it steps back to the room. */
  async deleteApp(app: string): Promise<void> {
    const snapshot = await this.backend.deleteApp(app);
    const focus = this.state.focus;
    const shown = (focus.kind === "app" || focus.kind === "play" || focus.kind === "make") && focus.app === app;
    this.set(shown ? { snapshot, focus: { kind: "room" } } : { snapshot });
  }

  async setHandle(handle: string): Promise<void> {
    this.set({ snapshot: await this.backend.setHandle(handle) });
  }

  async signOut(): Promise<void> {
    await this.backend.signOut();
  }

  /** Pull the latest snapshot; a no-op for listeners when nothing changed. */
  async refresh(): Promise<void> {
    const snapshot = await this.backend.snapshot();
    // `rev` covers this user's own records. Another author's membership can
    // run out with no write here, and their games then leave the shelf, so
    // the contents are compared as well.
    const current = this.state.snapshot;
    if (snapshot.rev === current.rev && JSON.stringify(snapshot) === JSON.stringify(current)) return;
    // A game whose card is open and that the room can no longer open (it left the shelf, Pocket TV's sample moved on) closes its card.
    const focus = this.state.focus;
    const gone = focus.kind === "app" && !snapshot.apps.some((entry) => entry.id === focus.app) && !snapshot.aired?.some((entry) => entry.id === focus.app);
    this.set(gone ? { snapshot, focus: { kind: "room" } } : { snapshot });
  }
}
